<template>
  <v-dialog :value="value" max-width="680" scrollable persistent @input="$emit('input', $event)">
    <v-card class="rounded-lg">
      <div class="analysis-dialog__header">
        <div class="analysis-dialog__heading">
          <div class="text-overline grey--text text--darken-1 analysis-dialog__overline">{{ overline }}</div>
          <div class="text-h6 font-weight-bold">{{ mine ? 'Alterar manifestação' : 'Manifestar-se' }}</div>
          <div v-if="deadline" class="text-caption grey--text text--darken-1">
            Prazo até {{ $moment(deadline).format('DD/MM/YYYY') }}. Você pode alterar a manifestação até a análise da Administração Judicial.
          </div>
        </div>
        <v-btn icon aria-label="Fechar" :disabled="saving" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <v-divider />

      <v-card-text class="pa-5">
        <div class="objection-field__label mb-1">{{ question }}</div>
        <v-radio-group v-model="form.status" class="mt-0" hide-details>
          <v-radio label="Concordo" :value="1" />
          <v-radio label="Não concordo" :value="2" />
          <v-radio label="Concordo parcialmente" :value="3" />
        </v-radio-group>

        <v-text-field v-if="form.status === 3" v-model="form.amount" class="mt-4" outlined dense prefix="R$"
          label="Valor com o qual você concorda" placeholder="0,00" inputmode="decimal" hide-details="auto" />

        <v-textarea v-model="form.reason" class="mt-4" outlined auto-grow rows="4" counter="5000" maxlength="5000"
          label="Justificativa" placeholder="Explique os motivos da sua manifestação." />

        <div class="objection-field__label mt-2 mb-1">Documentos (opcional)</div>
        <div v-for="doc in keptDocs" :key="doc.key" class="d-flex align-center manifestation-doc">
          <v-icon small class="mr-2">mdi-file-document-outline</v-icon>
          <span class="text-body-2 flex-grow-1">{{ doc.name }}</span>
          <v-btn icon small aria-label="Remover documento" @click="keptDocs = keptDocs.filter(item => item.key !== doc.key)"><v-icon small>mdi-close</v-icon></v-btn>
        </div>
        <v-file-input v-model="form.files" class="mt-2" outlined dense multiple chips small-chips
          accept="application/pdf,image/jpeg,image/png" prepend-icon="" prepend-inner-icon="mdi-paperclip"
          label="Anexar documentos" hint="PDF, JPG ou PNG, até 10 MB cada, no máximo 10 documentos." persistent-hint />

        <v-alert v-if="error" type="error" dense text class="mt-4 mb-0">{{ error }}</v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-spacer />
        <v-btn text :disabled="saving" @click="$emit('input', false)">Cancelar</v-btn>
        <v-btn color="primary" elevation="0" :loading="saving" @click="submit">{{ mine ? 'Salvar alteração' : 'Enviar manifestação' }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

const MAX_FILE = 10 * 1024 * 1024;

// Manifestação (contraditório) do credor ou do procurador sobre uma CHC ou um comprovante.
export default {
  name: 'ManifestationDialog',
  props: {
    value: { type: Boolean, default: false },
    endpoint: { type: String, required: true },
    mine: { type: Object, default: null },
    deadline: { type: [String, Date], default: null },
    overline: { type: String, default: '' },
    question: { type: String, default: 'Você concorda?' },
    registration: { type: String, default: null },
  },
  data: () => ({ form: { status: null, amount: '', reason: '', files: [] }, keptDocs: [], saving: false, error: '' }),
  watch: {
    value: { immediate: true, handler(open) { if (open) this.reset(); } },
  },
  methods: {
    reset() {
      const mine = this.mine;
      this.form = {
        status: mine ? mine.status : null,
        amount: mine && mine.amount ? Number(mine.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '',
        reason: mine ? mine.reason || '' : '',
        files: [],
      };
      this.keptDocs = mine ? [...(mine.docs || [])] : [];
      this.error = '';
    },
    validate() {
      if (!this.form.status) return 'Informe se concorda, não concorda ou concorda parcialmente.';
      if (this.form.status === 3 && !String(this.form.amount || '').trim()) return 'Informe o valor com o qual você concorda.';
      if (String(this.form.reason || '').trim().length < 10) return 'Escreva a justificativa (mínimo de 10 caracteres).';
      const files = this.form.files || [];
      if (files.some(file => file.size > MAX_FILE)) return 'Cada documento deve ter até 10 MB.';
      if (files.length + this.keptDocs.length > 10) return 'Envie no máximo 10 documentos.';
      return '';
    },
    async submit() {
      this.error = this.validate();
      if (this.error) return;
      const body = new FormData();
      body.append('status', this.form.status);
      if (this.form.status === 3) body.append('amount', this.form.amount);
      body.append('reason', this.form.reason);
      if (this.registration) body.append('registration', this.registration);
      body.append('keep_docs', JSON.stringify(this.keptDocs.map(doc => doc.key)));
      (this.form.files || []).forEach(file => body.append('files[]', file));
      this.saving = true;
      try {
        const result = await axios.post(this.endpoint, body);
        this.$emit('saved', result.data.content);
        this.$emit('input', false);
      } catch (error) {
        const content = error.response && error.response.data && error.response.data.content;
        this.error = (content && content.error && content.error.message) || 'Não foi possível enviar a manifestação. Tente novamente.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style>
.manifestation-doc { border: 1px solid #e0e0e0; border-radius: 6px; padding: 4px 4px 4px 10px; margin-bottom: 6px; }
</style>
